import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Api from "../../Api";
import EntitySearch from "../../parts/EntitySearch";
import Fab from "../../parts/Fab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import Section from "../../parts/Section";
import DiscreetMessage from "../../parts/DiscreetMessage";
import { ClientsSearchResponse } from "../../Api";
import { Client, Company } from "../../types";
import _ from "lodash";

const ClientsList = () => {
  const [data, setData] = useState<ClientsSearchResponse[] | null>(null);
  const [companies, setCompanies] = useState<Company[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const companyUuid = useQuery("empresa") ?? "";

  const searchFunction = useCallback(
    (searchTerm: string, page: string) =>
      Api.clientsSearch(companyUuid, searchTerm, page),
    [companyUuid]
  );

  useEffect(() => {
    Api.getCompanies().then(setCompanies);
  }, []);

  const loading = !data || !companies;

  return (
    <div>
      <div className="border-b">
        <Section className="p-4">
          <EntitySearch searchFunction={searchFunction} setEntities={setData} />
        </Section>
      </div>

      <Section className="p-4">
        <div className="flex flex-col gap-4 pb-32">
          {loading && <DiscreetMessage text="Carregando ..." />}

          {!loading &&
            data.length > 0 &&
            data.map(({ uuid, client, sellerName }) => (
              <ClientCard
                key={uuid}
                client={client}
                company={
                  _.find(companies, { uuid: client.companyUuid })!.shortName
                }
                sellerName={sellerName}
                expanded={expanded === client.uuid}
                setExpanded={(expanded) =>
                  expanded ? setExpanded(client.uuid) : setExpanded(null)
                }
              />
            ))}

          {!loading && data.length === 0 && (
            <DiscreetMessage text="Nenhum resultado" />
          )}
        </div>

        <Fab href="/clientes/criar" />
      </Section>
    </div>
  );
};

export default ClientsList;

const ClientCard = ({
  client,
  company,
  sellerName,
  expanded,
  setExpanded,
}: {
  client: Client;
  company: string;
  sellerName: string;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}) => (
  <div className="border rounded-lg shadow-sm">
    <div
      className={classNames("rounded-lg p-4  flex flex-col gap-2", {
        "hover:bg-slate-50 cursor-pointer": !expanded,
      })}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="text-xs text-gray-400">
        Desde{" "}
        <span className="font-bold">
          {moment(client.since).format("MM/YYYY")}
        </span>
      </div>

      <div className="overflow-scroll whitespace-nowrap no-scrollbar font-semibold">
        {client.name}
      </div>
    </div>

    {expanded && (
      <>
        <div className="p-4 border-t text-sm flex flex-col gap-4">
          <div>
            <div className="font-semibold">
              {client.type === "pj" ? "CNPJ" : "CPF"}
            </div>
            <div>{client.document}</div>
          </div>

          <div>
            <div className="font-semibold">Endereço</div>
            <div>
              {client.street}, {client.number}, {client.area}
            </div>
            <div>
              {client.city}, {client.state} - {client.zipcode}
            </div>
            {client.complement && (
              <div className="text-gray-500">{client.complement}</div>
            )}
          </div>

          <div>
            <div className="font-semibold">Contato</div>
            {client.contactName && (
              <div className="truncate">{client.contactName}</div>
            )}
            {client.email && <div>{client.email}</div>}
            {client.mobile && <div>{client.mobile}</div>}
            {client.phone && <div>{client.phone}</div>}
          </div>

          <div>
            <div className="font-semibold">Empresa</div>
            <div>{company}</div>
          </div>

          <div>
            <div className="font-semibold">Consultor</div>
            {sellerName ? (
              <div>{sellerName}</div>
            ) : (
              <div className="text-gray-500 italic">Nenhum</div>
            )}
          </div>
        </div>

        <div className="flex border-t text-xs">
          <Link
            className="border-r rounded-bl-lg p-3 font-bold hover:bg-slate-50"
            to={`/clientes/${client.uuid}`}
          >
            Atualizar
          </Link>

          <button
            className="rounded-br-lg p-3 hover:bg-slate-50 flex-1 text-right"
            onClick={() => setExpanded(false)}
          >
            <FontAwesomeIcon icon="chevron-up" />
          </button>
        </div>
      </>
    )}
  </div>
);

const useQuery = (param: string) => {
  return new URLSearchParams(useLocation().search).get(param);
};
